import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { del, patch, post } from "common/helpers/HTTP";
import { getSchemaMaxLength } from "common/helpers/utils";

import { CreateListSchema, type CreateListSchemaType } from "../schemas";

import type { LeadList } from "../types";

const COPY_NAME_PATTERN = /^(.+) \[Copy(?: (\d+))?\]$/;
const MAX_LIST_NAME_LENGTH = getSchemaMaxLength(CreateListSchema.entries.name);

// Example: "My List" -> "My List [Copy]" -> "My List [Copy 2]" etc.
function getDuplicateListName(originalName: string, existingNames: string[]) {
  const match = originalName.match(COPY_NAME_PATTERN);
  const baseName = match ? match[1] : originalName;

  const highestCopyNumber = existingNames.reduce((highest, existingName) => {
    const existingMatch = existingName.match(COPY_NAME_PATTERN);
    if (!existingMatch || existingMatch[1] !== baseName) return highest;
    return Math.max(highest, existingMatch[2] ? Number(existingMatch[2]) : 1);
  }, 0);

  const nextCopyNumber = highestCopyNumber + 1;
  const suffix = nextCopyNumber === 1 ? " [Copy]" : ` [Copy ${nextCopyNumber}]`;

  const maxBaseLength = MAX_LIST_NAME_LENGTH - suffix.length;
  const truncatedBaseName =
    baseName.length > maxBaseLength
      ? baseName.slice(0, maxBaseLength).trimEnd()
      : baseName;

  return `${truncatedBaseName}${suffix}`;
}

async function createLeadListRequest(
  workspaceId: string,
  newList: CreateListSchemaType,
): Promise<LeadList> {
  return (await post(`workspaces/${workspaceId}/lists`, newList)).list;
}

async function renameLeadListRequest(
  workspaceId: string,
  listId: string,
  name: string,
) {
  await patch(`workspaces/${workspaceId}/lists/${listId}`, { name });
}

async function deleteLeadListRequest(workspaceId: string, listId: string) {
  await del(`workspaces/${workspaceId}/lists/${listId}`);
}

async function duplicateLeadListRequest(
  workspaceId: string,
  listId: string,
  updates: { name: string; duplicateSources: boolean },
): Promise<LeadList> {
  return (
    await post(`workspaces/${workspaceId}/lists/${listId}/duplicate`, {
      name: updates.name,
      duplicate_sources: updates.duplicateSources,
    })
  ).list;
}

export default function useLeadListMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { listsKeys } = getQueryKeys(workspaceId);

  const { mutateAsync: createList, isPending: isCreatingList } = useMutation({
    mutationFn: (newList: CreateListSchemaType) =>
      createLeadListRequest(workspaceId, newList),
    onSuccess: (list) => {
      queryClient.setQueryData(listsKeys.list, (prevLists: LeadList[]) => [
        ...(prevLists ?? []),
        list,
      ]);
    },
    onError: () => toast.error("Failed to create list"),
  });

  const { mutateAsync: renameList, isPending: isRenamingList } = useMutation({
    mutationFn: ({ listId, name }: { listId: string; name: string }) =>
      renameLeadListRequest(workspaceId, listId, name),
    onSuccess: (_, { listId, name }) => {
      queryClient.setQueryData(listsKeys.list, (prevLists: LeadList[]) =>
        (prevLists ?? []).map((list) =>
          list.id === listId ? { ...list, name } : list,
        ),
      );
      queryClient.setQueryData(
        listsKeys.details(listId),
        (prevList: LeadList | undefined) =>
          prevList ? { ...prevList, name } : prevList,
      );
    },
    onError: () => toast.error("Failed to rename list"),
  });

  const { mutateAsync: deleteList, isPending: isDeletingList } = useMutation({
    mutationFn: (listId: string) => deleteLeadListRequest(workspaceId, listId),
    onSuccess: (_, listId) => {
      queryClient.setQueryData(listsKeys.list, (prevLists: LeadList[]) =>
        (prevLists ?? []).filter((list) => list.id !== listId),
      );
      queryClient.removeQueries({ queryKey: listsKeys.details(listId) });
      toast.success("List deleted");
    },
    onError: () => toast.error("Failed to delete list"),
  });

  const { mutateAsync: duplicateList, isPending: isDuplicatingList } =
    useMutation({
      mutationFn: ({
        listId,
        name,
        duplicateSources,
      }: {
        listId: string;
        name: string;
        duplicateSources: boolean;
      }) => {
        const existingNames = (
          queryClient.getQueryData<LeadList[]>(listsKeys.list) ?? []
        ).map((list) => list.name);

        return duplicateLeadListRequest(workspaceId, listId, {
          name: getDuplicateListName(name, existingNames),
          duplicateSources,
        });
      },
      onSuccess: (list) => {
        queryClient.setQueryData(listsKeys.list, (prevLists: LeadList[]) => [
          ...(prevLists ?? []),
          list,
        ]);
        toast.success("List duplicated");
      },
      onError: () => toast.error("Failed to duplicate list"),
    });

  return {
    createList,
    isCreatingList,
    renameList,
    isRenamingList,
    deleteList,
    isDeletingList,
    duplicateList,
    isDuplicatingList,
  };
}
